import {
  AloftPrice,
  orUndefined,
  schAlertType,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';

const ADJUSTMENT_THRESHOLD = 0.2;
const TARGET_CELL_TOOLTIP = 'Adjustment is unusually high';
const TIPS = 'Please explain why the high adjustment is necessary, or correct the adjustment.';

const ADJUSTMENT_FIELDS = [
  { key: 'quality', label: 'Quality' },
  { key: 'condition', label: 'Condition' },
] as const;

export const majorCompAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  fullCompSequence,
  compType,
  propertyDataEntities,
  order,
}) => {
  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order });

  return compSequence.flatMap((id) => {
    const propertyDataState = propertyDataEntities[id] as CompPropertyDataState | undefined;
    const salePrice = orUndefined(AloftPrice.schema.parse)(
      propertyDataState?.compData?.editableData?.transaction?.price,
    );
    if (!salePrice || salePrice.equals(AloftPrice.zero)) return [];

    const compAdjustment = compAdjustments[id];
    if (!compAdjustment) return [];

    return ADJUSTMENT_FIELDS.flatMap(({ key, label }) => {
      const adjustment = orUndefined(AloftPrice.schema.parse)(compAdjustment[key]?.adjustment);
      if (!adjustment) return [];

      const ratio = adjustment.ratio(salePrice);
      if (ratio == null || Math.abs(ratio) <= ADJUSTMENT_THRESHOLD) return [];

      return [
        {
          type: getScopedAlertType(schAlertType.enum.majorCompAdjustment, compType),
          status: 'active' as const,
          severity: 'error' as const,
          orderType: ['appraisify'] as const,
          message: `${getComparableDisplayName(id, fullCompSequence ?? compSequence)} has an unusually high ${label} adjustment`,
          propertyID: id,
          ...(compType ? { compType } : {}),
          fixingInfo: {
            tips: TIPS,
            pairedCostRouteItem: fixRoute,
            fixingMethod: ['comment', 'action'] as const,
            commentAlwaysRequired: false,
            targetCellKey: `${id}-${key}` as PropertyGridCellKey,
            targetCellTooltip: TARGET_CELL_TOOLTIP,
          },
          triggers,
        },
      ];
    });
  });
};
